import React, { useState } from 'react';
import { CREW, IMAGES, MISSION_CREW, OTHER_PLAYERS, CREW_HQ_CHAT, LEADERBOARD, MISSIONS } from '../mock/data';
import { useNavigate } from 'react-router-dom';
import { useDemo } from '../context/DemoContext';
import { Reveal, Eyebrow, StatusDot, ComingSoon, difficultyColor } from '../components/xc/Bits';
import CrewChat from '../components/xc/CrewChat';
import { useToast } from '../hooks/use-toast';
import { Toaster } from '../components/ui/toaster';
import { Shield, Trophy, Users, TrendingUp, MapPin, ArrowUpRight, Crown, Coins } from 'lucide-react';

const TABS = ['Overview', 'Members', 'Chat', 'Missions', 'Rankings'];
const ALL_MEMBERS = [...MISSION_CREW, ...OTHER_PLAYERS.map((p) => ({ ...p, role: 'Member' }))];

function Stat({ icon: Icon, label, value, color }) {
  return (
    <div className="xc-glass rounded-xl p-4">
      <Icon className="h-5 w-5 mb-2" style={{ color }} />
      <div className="text-[11px] text-white/40 uppercase tracking-wide">{label}</div>
      <div className="font-display font-700 text-xl text-white mt-0.5">{value}</div>
    </div>
  );
}

export default function Crew() {
  const [tab, setTab] = useState('Overview');
  const { toast } = useToast();
  const nav = useNavigate();

  return (
    <div className="bg-[#070609] min-h-screen">
      <Toaster />
      {/* HQ hero */}
      <section className="relative h-[46vh] min-h-[360px] overflow-hidden">
        <img src={IMAGES.redRoom} alt="HQ" className="absolute inset-0 h-full w-full object-cover" />
        <div className="absolute inset-0" style={{ background: 'linear-gradient(180deg,rgba(7,6,9,0.55),rgba(7,6,9,0.4),#070609)' }} />
        <div className="absolute inset-0 vignette" />
        <div className="relative max-w-[1440px] mx-auto px-4 lg:px-8 h-full flex flex-col justify-end pb-8">
          <div className="flex items-center gap-3">
            <div className="h-14 w-14 rounded-xl grid place-items-center flex-shrink-0" style={{ background: 'linear-gradient(135deg,#D90035,#7A18FF)' }}><Shield className="h-7 w-7 text-white" /></div>
            <div>
              <Eyebrow>Crew Headquarters · Downtown</Eyebrow>
              <h1 className="font-display font-700 text-4xl md:text-5xl text-white tracking-tight">THE SYNDICATE</h1>
            </div>
          </div>
        </div>
      </section>

      <section className="max-w-[1440px] mx-auto px-4 lg:px-8 py-8">
        {/* Stat strip */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          <Stat icon={TrendingUp} label="Crew Level" value={CREW.level} color="#B026FF" />
          <Stat icon={Users} label="Members" value={`${CREW.members} / ${CREW.maxMembers}`} color="#FF204E" />
          <Stat icon={Trophy} label="Reputation" value={CREW.reputation.toLocaleString()} color="#E0B85A" />
          <Stat icon={Crown} label="Xincity Rank" value={`#${CREW.rank}`} color="#E0B85A" />
          <Stat icon={MapPin} label="Headquarters" value={CREW.headquarters} color="#B026FF" />
        </div>

        {/* Tabs */}
        <div className="flex gap-2 mt-8 mb-6 border-b border-white/8 overflow-x-auto no-scrollbar">
          {TABS.map((t) => (
            <button key={t} onClick={() => setTab(t)} className={`px-4 py-3 text-sm font-500 relative whitespace-nowrap transition-colors ${tab === t ? 'text-white' : 'text-white/45 hover:text-white/75'}`}>
              {t}{tab === t && <span className="absolute bottom-0 left-0 right-0 h-0.5" style={{ background: 'linear-gradient(90deg,#FF204E,#B026FF)' }} />}
            </button>
          ))}
        </div>

        {tab === 'Overview' && (
          <div className="grid lg:grid-cols-[1fr_360px] gap-6">
            <div className="space-y-4">
              <div className="xc-card rounded-2xl overflow-hidden">
                <img src={IMAGES.lounge} alt="" className="h-52 w-full object-cover" />
                <div className="p-6">
                  <h3 className="font-display font-700 text-xl text-white">The Operations Room</h3>
                  <p className="text-white/55 mt-2 leading-relaxed text-sm">Beneath a Downtown high-rise, polished black surfaces reflect red ambient light. Purple displays track every district. This is where The Syndicate plans its next move.</p>
                  <button onClick={() => toast({ title: 'Coming Soon', description: 'HQ upgrades arrive in a future season.' })} className="btn-gold rounded-lg px-5 py-2.5 text-sm font-600 mt-4 flex items-center gap-2"><ArrowUpRight className="h-4 w-4" /> UPGRADE HQ</button>
                </div>
              </div>
              <div className="grid sm:grid-cols-2 gap-4">
                <div className="xc-glass rounded-2xl p-5"><Coins className="h-5 w-5 text-[#E0B85A] mb-2" /><div className="font-display font-700 text-white">Loyalty Pool</div><p className="text-sm text-white/50 mt-1">Crew rewards accumulate here after each successful mission.</p></div>
                <button data-testid="crew-territory-wars-card" onClick={() => nav('/competitions/territory-wars')} className="xc-glass rounded-2xl p-5 text-left hover:border-[#B026FF]/50 transition-colors"><Trophy className="h-5 w-5 text-[#B026FF] mb-2" /><div className="font-display font-700 text-white flex items-center gap-2">Territory Wars <span className="inline-flex items-center text-[10px] font-600 tracking-[0.18em] uppercase px-2.5 py-1 rounded-full border border-[#2FBF71]/40 text-[#2FBF71] bg-[#2FBF71]/8">Preview Live</span></div><p className="text-sm text-white/50 mt-1">Compete for visible control of Xincity districts.</p></button>
              </div>
            </div>
            <CrewChat label="CREW CHAT — THE SYNDICATE" seed={CREW_HQ_CHAT} className="h-fit" />
          </div>
        )}

        {tab === 'Members' && (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {ALL_MEMBERS.map((p, i) => (
              <Reveal key={p.id} delay={i * 40}>
                <div className="xc-card rounded-xl p-4 flex items-center gap-3">
                  <span className="h-11 w-11 rounded-full grid place-items-center text-sm font-600 text-white flex-shrink-0" style={{ background: p.role === 'Leader' ? 'linear-gradient(135deg,#E0B85A,#D6A84B)' : 'linear-gradient(135deg,#2a2140,#151119)', color: p.role === 'Leader' ? '#1a1206' : '#fff' }}>{p.name[0]}</span>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2"><span className="font-500 text-white">{p.name}</span>{p.role === 'Leader' && <Crown className="h-3.5 w-3.5 text-[#E0B85A]" />}</div>
                    <div className="flex items-center gap-1.5 text-xs text-white/50 mt-0.5"><StatusDot status={p.status} /> {p.status} · Lvl {p.level}</div>
                  </div>
                  <span className="text-[10px] text-white/40 uppercase">{p.role}</span>
                </div>
              </Reveal>
            ))}
          </div>
        )}

        {tab === 'Chat' && <div className="max-w-2xl"><CrewChat label="CREW CHAT — THE SYNDICATE" seed={CREW_HQ_CHAT} /></div>}

        {tab === 'Missions' && (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {MISSIONS.map((m) => (
              <div key={m.id} className="xc-card rounded-2xl p-5">
                <div className="flex items-center gap-2 mb-2"><span className="text-[10px] px-2 py-0.5 rounded-full" style={{ color: difficultyColor(m.difficulty), border: `1px solid ${difficultyColor(m.difficulty)}55` }}>{m.difficulty}</span>{m.status !== 'Available' && <ComingSoon />}</div>
                <h3 className="font-display font-700 text-white">{m.title}</h3>
                <p className="text-sm text-white/50 mt-2">{m.blurb}</p>
                <div className="mt-3 text-sm text-[#E0B85A] flex items-center gap-1.5"><Coins className="h-4 w-4" /> {m.reward.toLocaleString()} LC each</div>
              </div>
            ))}
          </div>
        )}

        {tab === 'Rankings' && (
          <div className="max-w-2xl xc-glass rounded-2xl overflow-hidden">
            <div className="px-5 py-3 border-b border-white/8 text-xs tracking-[0.16em] uppercase text-white/45">Global Crew Leaderboard</div>
            {LEADERBOARD.map((r) => (
              <div key={r.rank} className={`flex items-center justify-between px-5 py-4 border-b border-white/5 last:border-0 ${r.self ? 'bg-[#FF204E]/8' : ''}`}>
                <div className="flex items-center gap-4">
                  <span className="font-display font-700 text-lg w-8" style={{ color: r.self ? '#FF204E' : '#ffffff70' }}>#{r.rank}</span>
                  <span className={`font-500 ${r.self ? 'text-white' : 'text-white/75'}`}>{r.crew}{r.self && <span className="ml-2 text-[10px] text-[#FF204E]">YOU</span>}</span>
                </div>
                <span className="text-[#E0B85A] font-600">{r.rep.toLocaleString()}</span>
              </div>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}
